'use client';

import React from 'react';
import Link from 'next/link';
import { ReferenceChrome, Container, SectionLabel, SectionRule } from '@/components/pages/reference-landing/ReferenceChrome';
import { NavbarRef } from '@/components/pages/reference-landing/sections/NavbarRef';
import { FooterRef } from '@/components/pages/reference-landing/sections/FooterRef';

function CheckIcon() {
  return (
    <svg className="mt-0.5 size-4 shrink-0 text-[#129990]" viewBox="0 0 24 24" fill="none">
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="size-5 shrink-0 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function WhyRankGripContent() {
  const comparisonRows = [
    { title: 'Monthly cost', rankgrip: '$10–$30', traditional: '$19–$49', agencies: '$199–$499+' },
    { title: 'SEO Autopilot (no manual writing)', rankgrip: true, traditional: false, agencies: false },
    { title: 'Competitor keyword gap scanner', rankgrip: true, traditional: false, agencies: false },
    { title: 'AI content with images & links', rankgrip: true, traditional: false, agencies: 'Some only' },
    { title: 'Team collaboration', rankgrip: 'Unlimited Seats', traditional: 'Limited/None', agencies: 'Per-Seat ($$$)' },
    { title: 'Google Rank Tracking', rankgrip: true, traditional: false, agencies: 'Vague scores' },
    { title: 'CMS direct integrations', rankgrip: 'WordPress, Webflow, Shopify', traditional: 'None', agencies: 'WordPress only' },
    { title: 'Support', rankgrip: 'Founder access', traditional: 'Email only', agencies: 'Ticket system' },
    { title: 'Setup time', rankgrip: '60 seconds', traditional: '5–10 minutes', agencies: 'Hours/Days' },
    { title: 'Focus', rankgrip: 'SEO Workflow', traditional: 'Monitoring only', agencies: 'Feature bloat' },
  ];

  function Cell({ value }: { value: boolean | string }) {
    if (typeof value === 'boolean') {
      return value ? <CheckIcon /> : <XIcon />;
    }
    return <span className="text-sm font-light text-gray-600">{value}</span>;
  }

  return (
    <ReferenceChrome>
      <NavbarRef />

      {/* ── Hero ── */}
      <section className="relative w-full pt-14 pb-0 sm:pt-16">
        
        <Container className="relative z-10 text-center">
          <h1 className="mx-auto mt-14 max-w-3xl text-[2.5rem] font-normal leading-[1.15] tracking-tight text-gray-900 sm:text-5xl md:text-[3.5rem]">
            <span className="block">SEO on Autopilot.</span>
            <span className="block text-[#129990]">No Manual Writing Required.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base font-light leading-relaxed text-gray-500 sm:text-lg">
            Other tools trap you in manual keyword research. RankGrip is the SEO Autopilot platform that scans competitor gaps, writes AI-optimized blog posts, and publishes to your CMS on autopilot.
          </p>
        </Container>
      </section>

      <main>
        {/* ── Value Proposition ── */}
        <section className="py-16 sm:py-20">
          <Container className="text-center">
            <SectionLabel>Difference</SectionLabel>
            <h2 className="mx-auto mt-5 max-w-2xl text-2xl font-normal leading-snug tracking-tight text-gray-900 sm:text-[2rem]">
              The RankGrip Difference
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base font-light leading-relaxed text-gray-500">
              Frustrated with tools that create more work? RankGrip is the autopilot platform that transforms keyword research and blog writing from manual chores into automated business growth.
            </p>
          </Container>

          <Container className="mt-10">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {[
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                    </svg>
                  ),
                  title: 'SEO Autopilot: No Manual Writing',
                  desc: 'Other tools show you keyword metrics. We execute. Find competitor keyword gaps, then let our writing engine write and publish fully-formatted blog posts directly to your CMS. No manual drafts required.',
                },
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
                    </svg>
                  ),
                  title: 'Unlimited Seats: No Collaboration Tax',
                  desc: 'Mid-market tools punish collaboration with expensive per-seat pricing. RankGrip includes unlimited team seats on ALL plans—Starter and Pro. Scale your team without scaling your bill.',
                },
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
                    </svg>
                  ),
                  title: 'Founders You Can Talk To',
                  desc: 'Enterprise tools hide behind slow ticket systems. We answer support emails personally and host weekly Office Hours. Building in public means you get direct access to founders who actually care.',
                },
              ].map((card) => (
                <div key={card.title} className="rounded-sm border border-gray-200 bg-white p-6 sm:p-8">
                  <span className="flex size-9 items-center justify-center rounded-sm border border-gray-200 bg-gray-50 text-[#129990]">
                    {card.icon}
                  </span>
                  <h3 className="mt-4 text-xl font-normal text-gray-900">{card.title}</h3>
                  <p className="mt-2 text-sm font-light leading-relaxed text-gray-500">{card.desc}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* ── Comparison Table ── */}
        <SectionRule />
        <section className="py-16 sm:py-20 bg-gray-50/60">
          <Container className="text-center">
            <SectionLabel>Comparison</SectionLabel>
            <h2 className="mx-auto mt-5 max-w-2xl text-2xl font-normal leading-snug tracking-tight text-gray-900 sm:text-[2rem]">
              Compare Your Options
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base font-light leading-relaxed text-gray-500">
              Budget tools just monitor. Mid-market tools suffer from feature bloat and per-seat pricing. RankGrip is the autopilot solution at startup-friendly cost.
            </p>
          </Container>

          <Container className="mt-10">
            <div className="overflow-hidden rounded-sm border border-gray-200">
              {/* Header */}
              <div className="grid grid-cols-4 gap-4 border-b border-gray-200 bg-gray-50/80 px-4 py-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-400" />
                <div className="text-center text-xs font-semibold uppercase tracking-wider text-[#129990]">RankGrip</div>
                <div className="text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Budget Tools ($19–49)</div>
                <div className="text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Mid-Market ($199+)</div>
              </div>
              {/* Rows */}
              {comparisonRows.map((row, i) => (
                <div
                  key={row.title}
                  className={`grid grid-cols-4 gap-4 border-b border-gray-100 px-4 py-3.5 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                >
                  <div className="text-sm font-medium text-gray-900">{row.title}</div>
                  <div className="flex justify-center"><Cell value={row.rankgrip} /></div>
                  <div className="flex justify-center"><Cell value={row.traditional} /></div>
                  <div className="flex justify-center"><Cell value={row.agencies} /></div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* ── Testimonials ── */}
        <section className="py-16 sm:py-20">
          <Container className="text-center">
            <SectionLabel>Testimonials</SectionLabel>
            <h2 className="mx-auto mt-5 max-w-2xl text-2xl font-normal leading-snug tracking-tight text-gray-900 sm:text-[2rem]">
              Real Results from Real Businesses
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base font-light leading-relaxed text-gray-500">
              Businesses using RankGrip&apos;s Autopilot to automate keyword gap targeting and scale content production.
            </p>
          </Container>

          <Container className="mt-10">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {[
                {
                  initials: 'SM', name: 'Sarah Martinez', role: 'Head of Growth, TechStart SaaS',
                  quote: 'Other tools handed us endless spreadsheets of data. RankGrip connected to Search Console, flagged our CTR drops, and gave us exact step-by-step page refresh briefs. Our organic traffic grew 3x in 90 days.',
                  metrics: [
                    { value: '2.4x', label: 'Organic CTR' },
                    { value: '+210%', label: 'Demo Requests' },
                  ],
                },
                {
                  initials: 'JC', name: 'James Chen', role: 'Founder, GrowthLab Agency',
                  quote: 'RankGrip\'s Search Console agent identifies decaying pages and competitor keyword gaps instantly. Instead of spending hours in GSC, we get prioritized daily tasks to fix title tags and refresh content.',
                  metrics: [
                    { value: '15 min', label: 'Daily Optimization' },
                    { value: '8→15', label: 'Active Clients' },
                  ],
                },
              ].map((t) => (
                <div key={t.name} className="rounded-sm border border-gray-200 bg-white p-6 sm:p-8">
                  <div className="flex items-start gap-4 mb-4">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-sm font-medium text-gray-600">
                      {t.initials}
                    </span>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">{t.name}</h4>
                      <p className="text-xs text-gray-500">{t.role}</p>
                    </div>
                  </div>
                  <blockquote className="text-sm font-light leading-relaxed text-gray-600 mb-6">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <div className="grid grid-cols-2 gap-3">
                    {t.metrics.map((m) => (
                      <div key={m.label} className="rounded-sm border border-gray-100 bg-gray-50 p-3 text-center">
                        <p className="text-lg font-light text-[#129990]">{m.value}</p>
                        <p className="text-xs text-gray-500">{m.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* ── Who It's For ── */}
        <SectionRule />
        <section className="py-16 sm:py-20 bg-gray-50/60">
          <Container className="text-center">
            <SectionLabel>Perfect For</SectionLabel>
            <h2 className="mx-auto mt-5 max-w-2xl text-2xl font-normal leading-snug tracking-tight text-gray-900 sm:text-[2rem]">
              Who RankGrip Is Perfect For
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base font-light leading-relaxed text-gray-500">
              Built for B2B SaaS, agencies, startups, and SMBs who need real results—not just more dashboards to check.
            </p>
          </Container>

          <Container className="mt-10">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {[
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  ),
                  title: 'B2B SaaS Companies',
                  desc: 'Capture high-intent searches, automate your blog writing, and track ranks in real-time. See exactly which keywords drive search impressions.',
                },
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
                    </svg>
                  ),
                  title: 'Agencies & Consultants',
                  desc: 'Scale blog production for clients with native Webflow/WordPress integrations. Manage multiple sites from one dashboard without expensive per-seat fees.',
                },
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                    </svg>
                  ),
                  title: 'Startups & SMBs',
                  desc: 'Get enterprise-level keyword gap scanning and automated blog writing at startup-friendly pricing. Dominate search results without expensive writers.',
                },
              ].map((card) => (
                <div key={card.title} className="rounded-sm border border-gray-200 bg-white p-6 sm:p-8">
                  <span className="flex size-9 items-center justify-center rounded-sm border border-gray-200 bg-gray-50 text-[#129990]">
                    {card.icon}
                  </span>
                  <div className="mt-4">
                    <h3 className="flex items-center gap-3 text-xl font-normal text-gray-900">{card.title}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-gray-500">{card.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* ── CTA ── */}
        <section className="py-16 sm:py-20 bg-gray-50/60">
          <Container className="text-center">
            <h2 className="text-2xl font-normal leading-tight tracking-tight text-gray-900 sm:text-[2.25rem]">
              Ready to Put Your SEO on Autopilot?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base font-light leading-relaxed text-gray-500">
              Join businesses using RankGrip to scan competitor gaps, write SEO content, and publish to their CMS on autopilot. 7-day free trial, cancel anytime.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="relative flex items-center justify-center rounded-md bg-[#129990] px-8 py-3 text-sm font-medium text-white shadow-sm transition-all duration-75 hover:bg-[#129990]/90 active:translate-y-[2px]"
              >
                Start Free Trial
              </Link>
              <Link
                href="/industry-solutions"
                className="relative flex items-center justify-center rounded-md border border-gray-200 bg-white px-8 py-3 text-sm font-medium text-gray-900 shadow-sm transition-all duration-75 hover:bg-gray-50 active:translate-y-[2px]"
              >
                Industry Solutions
              </Link>
            </div>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-6">
              {['7-day free trial', 'No credit card required', 'Cancel anytime'].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm font-light text-gray-500">
                  <svg className="size-4 shrink-0 text-[#129990]" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {item}
                </div>
              ))}
            </div>
          </Container>
        </section>
      </main>

      <FooterRef />
    </ReferenceChrome>
  );
}
